// HistoryList.tsx
import { useState, useEffect } from 'react';
import { HistoryItem, HistoryDetail } from '../types';
import { getHistory, getHistoryDetail, deleteHistory } from '../api';
import ReviewResult from './ReviewResult';

export default function HistoryList({ onBack }: { onBack: () => void }) {
  const [items, setItems] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<HistoryDetail | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  const loadHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getHistory();
      setItems(data.reviews);
    } catch (err: any) {
      setError(err.message || '加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadHistory(); }, []);

  const handleView = async (id: string) => {
    try {
      const detail = await getHistoryDetail(id);
      setSelected(detail);
    } catch (err: any) {
      setError(err.message || '获取详情失败');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('确定删除这条审查记录？')) return;
    setDeleting(id);
    try {
      await deleteHistory(id);
      setItems(items.filter(i => i.id !== id));
      if (selected?.id === id) setSelected(null);
    } catch (err: any) {
      setError(err.message || '删除失败');
    } finally {
      setDeleting(null);
    }
  };

  const riskEmoji = (level: string) => {
    switch (level) {
      case 'high': return '🔴';
      case 'medium': return '🟡';
      case 'low': return '🟢';
      default: return '⚪';
    }
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString('zh-CN', {
      month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  };

  if (selected) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <button
            onClick={() => setSelected(null)}
            className="text-blue-600 hover:underline mb-4 inline-block"
          >
            ← 返回历史列表
          </button>
          <div className="text-xs text-gray-400 mb-4">
            文件: {selected.filename} · {formatDate(selected.created_at)}
          </div>
          <ReviewResult result={selected.review_result} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-800">📋 审查历史</h2>
          <button
            onClick={onBack}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            返回上传
          </button>
        </div>

        {loading && (
          <div className="text-center py-12 text-gray-400">
            <div className="animate-spin text-3xl mb-2">⏳</div>
            加载中...
          </div>
        )}

        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-4">
            <p className="text-red-700">❌ {error}</p>
            <button onClick={loadHistory} className="text-sm text-red-600 hover:underline mt-1">
              重试
            </button>
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="text-center py-16 text-gray-400">
            <div className="text-5xl mb-4">📭</div>
            <p className="text-lg">暂无审查记录</p>
            <p className="text-sm mt-1">上传合同完成审查后，记录会自动保存</p>
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className="space-y-3">
            {items.map(item => (
              <div
                key={item.id}
                className="bg-white rounded-lg border border-gray-200 p-4 hover:border-blue-300 hover:shadow-sm transition cursor-pointer"
                onClick={() => handleView(item.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-gray-800 truncate">
                        {item.filename}
                      </span>
                      <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-600 rounded">
                        {item.contract_type || '未知'}
                      </span>
                    </div>
                    <div className="text-sm text-gray-500">
                      {item.parties?.length > 0 && (
                        <span>{item.parties.join(' · ')}</span>
                      )}
                      {item.amount && (
                        <span className="ml-2 text-gray-400">{item.amount}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-2 text-xs">
                      {item.risk_counts && Object.entries(item.risk_counts).map(([level, count]) => (
                        <span key={level} className="flex items-center gap-1">
                          {riskEmoji(level)} {count}
                        </span>
                      ))}
                      <span className="text-gray-400">
                        {formatDate(item.created_at)}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(item.id);
                    }}
                    disabled={deleting === item.id}
                    className="ml-3 p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition"
                    title="删除"
                  >
                    {deleting === item.id ? '⏳' : '🗑'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}